import _ from "lodash";
import { useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
} from "recharts";
import { Card, Typography } from "@mui/material";

import TimeUtils from "utils/time";
import AnalyticsService from "data/firestore/analytics";
import { MethodColors, MethodCategories } from "data/methods";

const getCategoryColor = (category) => {
  const palette =
    MethodColors[category] || MethodColors[MethodCategories.Other];
  return palette[500] || "#6c757d";
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const entry = payload[0].payload;
    const percentage = _.round((entry.value / entry.total) * 100, 1);
    const time = TimeUtils.elapsedToString(
      TimeUtils.breakdown(entry.value, true),
      2,
      true
    );

    AnalyticsService.throttleChartHover(
      "hover_method_breakdown",
      {},
      `${entry.name} ${time}`
    );

    return (
      <Card
        style={{
          backgroundColor: "rgba(0, 0, 0, 0.85)",
          borderRadius: 12,
          color: "white",
          padding: 10,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {entry.name}
        </Typography>
        <Typography variant="caption">
          {time} ({percentage}%)
        </Typography>
      </Card>
    );
  }
  return null;
};

/**
 * Horizontal bar chart for method/category breakdown.
 *
 * @param {Array} categories - [{ name, value, total }]
 * @param {Array} methods - [{ name, value, total, category }]
 * @param {boolean} showBreakdown - true = individual methods, false = categories only
 */
const MethodBarChart = ({ categories, methods, showBreakdown = false }) => {
  const data = useMemo(() => {
    if (showBreakdown) {
      return _.orderBy(
        methods.filter((m) => m.value > 0),
        "value",
        "desc"
      );
    }
    return _.orderBy(
      categories.filter((c) => c.value > 0),
      "value",
      "desc"
    );
  }, [categories, methods, showBreakdown]);

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 5, right: 20, bottom: 5, left: 5 }}
      >
        <XAxis type="number" hide />
        <YAxis
          type="category"
          dataKey="name"
          width={120}
          tick={{ fontSize: 12 }}
          tickLine={false}
          axisLine={false}
          interval={0}
        />
        <Tooltip
          content={<CustomTooltip />}
          cursor={{ fill: "rgba(0,0,0,0.05)" }}
        />
        <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={20}>
          {data.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={getCategoryColor(entry.category || entry.name)}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export default MethodBarChart;
